<script setup lang="ts">
import { api } from '@/api';
import { CertificateInfoDto } from '@/api/generator';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import { findValueInCommaSeperatedListByKey } from '@/utils/stringUtils.ts';
import { useLoading } from '@/utils/useLoading.ts';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify';
import SSLCertificateUploadForm from './components/SSLCertificateUploadForm.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';

const isCertificateValid = ref(true);

const { t } = useTranslation();

const EXPIRY_WARNING_DAYS = 30;

const certificateInfo = ref<CertificateInfoDto>();
const { action: loadInitialValues } = useLoading(async () => {
  const { result } = await api.tenantsApi.getKestrelCertificateSettings();

  if (result) {
    certificateInfo.value = result;
    isCertificateValid.value = !!result.isValid;
  }
}, true);

const isEditMode = ref(false);
const { smAndDown, mdAndDown } = useDisplay();
const notificationStore = useNotificationStore();
const subjectCommonName = computed(() => {
  try {
    return findValueInCommaSeperatedListByKey(certificateInfo.value?.subject, 'CN');
  } catch (error) {
    console.error('Error finding value in comma-separated list by key:', error);
    return undefined;
  }
});
const issuerOrganization = computed(() => {
  try {
    return findValueInCommaSeperatedListByKey(certificateInfo.value?.issuer, 'O');
  } catch (error) {
    console.error('Error finding value in comma-separated list by key:', error);
    return undefined;
  }
});

function handleSaved(result: CertificateInfoDto) {
  isEditMode.value = false;
  notificationStore.success(t('NewSSLCertificateAdded'));
  certificateInfo.value = result;
}
onMounted(() => loadInitialValues());
</script>

<template>
  <BasePage>
    <template #title>
      <BasePageTitle :title="$t('SSLCertificate')" />
    </template>

    <p class="mb-10 instruction">
      {{ $t('SSLCertificateSettingsMsg') }}
    </p>

    <v-sheet class="pa-8">
      <v-table :class="{ 'md-and-down': mdAndDown, 'sm-and-down': smAndDown }">
        <tbody>
          <tr>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-2">
                {{ $t('SubjectCommonName') }}
              </p>
              <p>{{ subjectCommonName }}</p>
            </td>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-2">
                {{ $t('Validity') }}
              </p>
              <p
                :class="{
                  'text-success': certificateInfo?.isValid,
                  'text-error': !certificateInfo?.isValid,
                }"
              >
                {{
                  certificateInfo?.isValid
                    ? $t('TheCertificateIsValid')
                    : $t('TheCertificateIsNotValid')
                }}
              </p>
            </td>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-3">
                {{ $t('IssuedBy') }}
              </p>
              <p>{{ issuerOrganization }}</p>
            </td>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-2">
                {{ $t('DateOfIssue') }}
              </p>
              <p>
                <DateTimeFormatter :value="certificateInfo?.notBefore" />
              </p>
            </td>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-2">
                {{ $t('CertificateExpiration') }}
              </p>
              <p>
                <DateTimeFormatter :value="certificateInfo?.notAfter" />
              </p>
            </td>
            <td class="py-3 py-lg-0">
              <p class="v-label-overwrite mb-2">
                {{ $t('ExpiresIn') }}
              </p>
              <p
                v-if="certificateInfo?.daysLeft"
                :class="{
                  'text-error': certificateInfo.daysLeft < EXPIRY_WARNING_DAYS,
                }"
              >
                {{
                  `${certificateInfo?.daysLeft} ${
                    certificateInfo?.daysLeft === 1 ? $t('Day_one') : $t('Day_other')
                  }`
                }}
              </p>
            </td>
            <td class="pt-3 pt-lg-0 text-right justify-end">
              <PrimaryVBtn
                :disabled="isEditMode"
                :text="$t('UploadSSLCertificate')"
                @click="isEditMode = !isEditMode"
              />
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-sheet>
    <SSLCertificateUploadForm
      v-if="isEditMode"
      @canceled="isEditMode = false"
      @saved="handleSaved"
    />
  </BasePage>
</template>

<style lang="scss" scoped>
.instruction {
  max-width: 50%;
}

.v-table {
  &.md-and-down {
    :deep(.v-table__wrapper) {
      overflow: visible;
    }

    tbody {
      display: flex;

      tr {
        display: flex;
        width: 100%;
        flex-wrap: wrap;

        & > td {
          width: 50%;
          height: auto;
          display: flex;
        }
      }
    }
  }
  &.sm-and-down {
    tbody {
      tr {
        & > td {
          width: 100%;
        }
      }
    }
  }
}
</style>
